import { composeFetch } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  setMethod,
  setServiceUrl,
  setQueryParams,
} from '@library/http/requests'

import type { MarketplaceInput, MarketplaceResponse } from './interfaces/marketplace'
import type {
  CategoryQueryInput,
  CategoryResponse,
  MenuQueryInput,
  MenuResponse,
  StandardRecipeItem,
  StandardRecipeTileQueryInput,
} from './interfaces/menu'
import type { RecipeQueryInput, RecipeQueryResponse } from './interfaces/recipe'

const getRecipeResponseParser = async (response: Response): Promise<RecipeQueryResponse> => {
  if (!response.ok) {
    const responseMsg = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMsg}`)
  }

  const parsedBody = await response.json()

  return { ...parsedBody, responseType: 'experience' } as RecipeQueryResponse
}

const getMenuResponseParser = async (response: Response): Promise<MenuResponse> => {
  if (!response.ok) {
    const responseMsg = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMsg}`)
  }

  const parsedBody = await response.json()

  return parsedBody as MenuResponse
}

const getCategoryResponseParser = async (response: Response): Promise<CategoryResponse> => {
  if (!response.ok) {
    const responseMsg = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMsg}`)
  }

  const parsedBody = await response.json()

  return parsedBody as CategoryResponse
}

const getStandardRecipeTileResponseParser = async (
  response: Response,
): Promise<StandardRecipeItem> => {
  if (!response.ok) {
    const responseMsg = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMsg}`)
  }

  const parsedBody = await response.json()

  return parsedBody as StandardRecipeItem
}

export const categoryFetcher = composeFetch(
  composeRequest<CategoryQueryInput>(
    setServiceUrl('menuexperience', 1),
    addPath((input) => `categories/${input.categoryId}`),
    goustoHeaders,
    setMethod('GET'),
    setQueryParams(({ numPortions, deliveryDate, menuId, preview }) => {
      return {
        numPortions: numPortions.toString(),
        deliveryDate,
        ...(menuId && { menuId }),
        ...(preview && { preview }),
      }
    }),
  ),
  getCategoryResponseParser,
)

export const menuFetcher = composeFetch(
  composeRequest<MenuQueryInput>(
    setServiceUrl('menuexperience', 1),
    addPath('menu'),
    goustoHeaders,
    setMethod('GET'),
    setQueryParams(({ numPortions, deliveryDate, categorySlug, preview }) => {
      return {
        numPortions: numPortions.toString(),
        deliveryDate,
        ...(categorySlug && { categorySlug }),
        ...(preview && { preview }),
      }
    }),
  ),
  getMenuResponseParser,
)

export const standardRecipeTileFetcher = composeFetch(
  composeRequest<StandardRecipeTileQueryInput>(
    setServiceUrl('menuexperience', 1),
    addPath((input) => `menu/recipe-grid/standard-recipe/${input.recipeId}`),
    goustoHeaders,
    setMethod('GET'),
    setQueryParams(({ menuId, numPortions, deliveryDate }) => {
      return {
        menuId,
        numPortions: numPortions.toString(),
        deliveryDate,
      }
    }),
  ),
  getStandardRecipeTileResponseParser,
)

export const recipeFetcher = composeFetch(
  composeRequest<RecipeQueryInput>(
    setServiceUrl('menuexperience', 1),
    addPath((input) => `recipes/${input.recipeId}`),
    goustoHeaders,
    setMethod('GET'),
    setQueryParams(({ numPortions, menuId, collectionId, deliveryDate }) => {
      return {
        numPortions: numPortions.toString(),
        menuId,
        ...(deliveryDate && { deliveryDate }),
        ...(collectionId && { collectionId }),
      }
    }),
  ),
  getRecipeResponseParser,
)

const getMarketplaceResponseParser = async (response: Response): Promise<MarketplaceResponse> => {
  if (!response.ok) {
    const responseMsg = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMsg}`)
  }

  return response.json() as Promise<MarketplaceResponse>
}

export const marketplaceFetcher = composeFetch(
  composeRequest<MarketplaceInput>(
    setServiceUrl('menuexperience', 1),
    addPath((input) => `orders/${input.orderId}/marketplace`),
    goustoHeaders,
    setMethod('GET'),
  ),
  getMarketplaceResponseParser,
)
